import {
  Container,
  Paper,
  Title,
  Text,
  Stack,
  Group,
  Badge,
  Code,
  Accordion,
  Box,
  LoadingOverlay,
  Alert,
  SimpleGrid,
  Card,
  Anchor,
  ThemeIcon,
} from "@mantine/core";
import { useEffect, useState } from "react";
import { useParams } from "wouter";
// import { fetchMonitorResultDetails } from "./brandsApi";
import {
  IconInfoCircle,
  IconBrain,
  IconChartLine,
  IconUsers,
  IconLink,
  IconExternalLink,
} from "@tabler/icons-react";
import BrandBreadcrumbs from "./BrandBreadcrumbs";
import AnswerMarkdown from "../_Shared/AnswerMarkdown";
import { PieChart } from "@mantine/charts";
import LlmProviderLogo from "../LlmProviders/LlmProviderLogo";
import { useMonitorsStore } from "./monitorsStore";
import { useBrandContext } from "./brandContext";

function SentimentBadge({ score, label, ...props }) {
  let color = "gray";
  if (label === "positive") {
    color = "green";
  }
  if (label === "negative") {
    color = "red";
  }
  return (
    <Badge color={color} {...props}>
      {(score * 100).toFixed(0)}%
    </Badge>
  );
}

export default function MonitorResultDetails() {
  const { brandId, monitorId, resultId } = useParams();
  const [result, setResult] = useState(null);
  const [llmRuns, setLlmRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { fetchMonitorResultDetails } = useMonitorsStore();
  const { selectBrandById } = useBrandContext();

  useEffect(() => {
    if (brandId) selectBrandById(brandId);
  }, [brandId, selectBrandById]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const data = await fetchMonitorResultDetails(
          brandId,
          monitorId,
          resultId
        );
        setResult(data.result);
        setLlmRuns(data.llmQueryRuns);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [brandId, monitorId, resultId]);

  if (loading) {
    return (
      <Container>
        <LoadingOverlay visible={true} />
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        {/* <BrandBreadcrumbs brandId={brandId} /> */}
        <Alert icon={<IconInfoCircle />} title="Error" color="red">
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl">
      {/* <BrandBreadcrumbs brandId={brandId} /> */}
      <Stack>
        <Group justify="space-between">
          <Title order={2}>Monitor Result Details</Title>
          <Badge size="lg" variant="light">
            {new Date(result.createdAt).toLocaleString()}
          </Badge>
        </Group>

        {/* Result Summary */}
        <SimpleGrid cols={4}>
          <Card withBorder>
            <Group>
              <IconChartLine size={24} />
              <Box>
                <Text size="xs" c="dimmed">
                  Sentiment Score
                </Text>
                <Text size="lg" fw={700}>
                  {(result.averageSentimentScore * 100).toFixed(1)}%
                </Text>
              </Box>
            </Group>
          </Card>

          <Card withBorder>
            <Group>
              <IconUsers size={24} />
              <Box>
                <Text size="xs" c="dimmed">
                  Mention Rank
                </Text>
                <Text size="lg" fw={700}>
                  {result.averageMentionRank.toFixed(2)}
                </Text>
              </Box>
            </Group>
          </Card>

          <Card withBorder>
            <Group>
              <IconChartLine size={24} />
              <Box>
                <Text size="xs" c="dimmed">
                  Share of Voice
                </Text>
                <Text size="lg" fw={700}>
                  {(result.averageShareOfVoice * 100).toFixed(1)}%
                </Text>
              </Box>
            </Group>
          </Card>

          <Card withBorder>
            <Group>
              <IconBrain size={24} />
              <Box>
                <Text size="xs" c="dimmed">
                  LLM Queries
                </Text>
                <Text size="lg" fw={700}>
                  {result.totalQueries}
                </Text>
              </Box>
            </Group>
          </Card>
        </SimpleGrid>

        {/* Brand Mentions */}
        <Paper p="md" withBorder>
          <Title order={3} mb="md">
            Brand Mentions
          </Title>
          <Group wrap="nowrap" gap="xl">
            <PieChart
              withTooltip
              data={(() => {
                const entries = Object.entries(result.brandCounts).sort(
                  ([, a], [, b]) => b - a
                );
                const top7 = entries.slice(0, 7);
                const others = entries.slice(7);
                const othersCount = others.reduce(
                  (sum, [, count]) => sum + count,
                  0
                );
                const pieData = top7.map(([name, count]) => ({
                  name,
                  value: count,
                  color: `hsl(${Math.random() * 360}, 60%, 60%)`,
                }));
                if (others.length > 0) {
                  pieData.push({
                    name: "Others",
                    value: othersCount,
                    color: `hsl(${Math.random() * 360}, 60%, 60%)`,
                  });
                }
                return pieData;
              })()}
            />

            <Group gap="sm">
              {(() => {
                const entries = Object.entries(result.brandCounts).sort(
                  ([, a], [, b]) => b - a
                );
                const top7 = entries.slice(0, 7);
                const others = entries.slice(7);
                const othersCount = others.reduce(
                  (sum, [, count]) => sum + count,
                  0
                );
                return (
                  <>
                    {top7.map(([brand, count]) => (
                      <Badge key={brand} size="lg" variant="outline">
                        {brand}: {count}
                      </Badge>
                    ))}
                    {others.length > 0 && (
                      <Badge key="others" size="lg" variant="outline">
                        Others: {othersCount}
                      </Badge>
                    )}
                  </>
                );
              })()}
            </Group>
          </Group>
        </Paper>

        {/* LLM Query Runs 
        
        TBD dont' show all of them here, but just the per-provider results
        */}
        <Paper p="md" withBorder>
          <Title order={3} mb="md">
            LLM Query Runs & Analysis
          </Title>

          <Accordion variant="separated">
            {llmRuns?.map((run, index) => (
              <Accordion.Item key={run.id} value={run.id}>
                <Accordion.Control>
                  <Group justify="space-between">
                    <Group>
                      <Text fw={500}>Query #{index + 1}</Text>
                      <LlmProviderLogo llmProvider={run.llmIdPath} />
                      <Text truncate="end">{run.prompt}</Text>
                    </Group>
                    <Group>
                      <SentimentBadge
                        score={run.analysis?.sentimentScore}
                        label={run.analysis?.sentimentLabel}
                      />
                      {/* <Badge color="green">
                        Confidence:{" "}
                        {(run.analysis?.confidence * 100).toFixed(0)}%
                      </Badge> */}
                      <Text size="sm" c="dimmed">
                        {new Date(run.createdAt).toLocaleTimeString()}
                      </Text>
                    </Group>
                  </Group>
                </Accordion.Control>

                <Accordion.Panel>
                  <Stack>
                    {/* Prompt */}
                    <Box>
                      <Text fw={600} mb="xs">
                        Prompt:
                      </Text>
                      <Paper p="sm" bg="gray.1">
                        <Text>{run.prompt}</Text>
                      </Paper>
                    </Box>

                    {/* LLM Response */}
                    <Box>
                      <Text fw={600} mb="xs">
                        LLM Response:
                      </Text>
                      {/* The same renderer the detection samples use: an answer's citations are
                          the reader's evidence, and they must read the same way everywhere. */}
                      <AnswerMarkdown
                        text={run.llmResponse}
                        highlightTerms={run.analysis?.brandMentions}
                      />
                    </Box>

                    {/* Analysis Results */}
                    <Box>
                      <Text fw={600} mb="xs">
                        Analysis Results:
                      </Text>
                      <SimpleGrid cols={2}>
                        <Paper p="sm" withBorder>
                          <Text size="sm" c="dimmed">
                            Extracted Brands
                          </Text>
                          <Group mt="xs">
                            {run.analysis?.brandMentions?.map((brand) => (
                              <Badge key={brand} size="sm" variant="dot">
                                {brand}
                              </Badge>
                            ))}
                          </Group>
                        </Paper>

                        <Paper p="sm" withBorder>
                          <Text size="sm" c="dimmed">
                            Metrics
                          </Text>
                          <Stack gap="xs" mt="xs">
                            <Group justify="space-between">
                              <Text size="sm">Sentiment:</Text>
                              <SentimentBadge
                                score={run.analysis?.sentimentScore}
                                label={run.analysis?.sentimentLabel}
                              />
                              {/*                               
                              <Badge
                                color={
                                  run.analysis?.sentimentLabel === "positive"
                                    ? "green"
                                    : "red"
                                }
                              >
                                {(run.analysis?.sentimentScore * 100).toFixed(
                                  2
                                )}
                                %
                              </Badge> */}
                            </Group>
                            <Group justify="space-between">
                              <Text size="sm">Mention Rank:</Text>
                              <Text size="sm">
                                {run.analysis?.mentionRank === 0
                                  ? "N/A"
                                  : run.analysis?.mentionRank.toFixed(0)}
                              </Text>
                            </Group>
                            <Group justify="space-between">
                              <Text size="sm">Share of Voice:</Text>
                              <Text size="sm">
                                {(run.analysis?.shareOfVoice * 100).toFixed(1)}%
                              </Text>
                            </Group>
                            {run.analysis?.recommendationRank != null && (
                              <Group justify="space-between">
                                <Text size="sm">Recommended:</Text>
                                <Badge
                                  color={run.analysis?.isRecommended ? "green" : "gray"}
                                  size="sm"
                                >
                                  {run.analysis?.isRecommended
                                    ? `#${run.analysis.recommendationRank}`
                                    : "No"}
                                </Badge>
                              </Group>
                            )}
                          </Stack>
                        </Paper>
                      </SimpleGrid>
                    </Box>

                    {/* Sources Cited */}
                    {(run.nativeCitations?.length > 0 ||
                      run.analysis?.extractedSources?.length > 0) && (
                      <Box>
                        <Text fw={600} mb="xs">
                          <Group gap="xs">
                            <IconLink size={16} />
                            Sources Cited
                          </Group>
                        </Text>
                        <Stack gap="xs">
                          {(run.nativeCitations?.length > 0
                            ? run.nativeCitations
                            : run.analysis?.extractedSources?.map((url) => ({
                                url,
                                title: null,
                                domain: null,
                              }))
                          )?.map((citation, i) => (
                            <Paper key={i} p="xs" withBorder>
                              <Group gap="xs" wrap="nowrap">
                                <ThemeIcon
                                  variant="light"
                                  color="blue"
                                  size="sm"
                                >
                                  <IconExternalLink size={12} />
                                </ThemeIcon>
                                <Box style={{ minWidth: 0, flex: 1 }}>
                                  <Anchor
                                    href={citation.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    size="sm"
                                    truncate="end"
                                    style={{ display: "block" }}
                                  >
                                    {citation.title || citation.url}
                                  </Anchor>
                                  {citation.title && (
                                    <Text size="xs" c="dimmed" truncate="end">
                                      {citation.domain ||
                                        new URL(citation.url).hostname}
                                    </Text>
                                  )}
                                </Box>
                              </Group>
                            </Paper>
                          ))}
                        </Stack>
                      </Box>
                    )}
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion>
        </Paper>

        {/* Raw Result Data */}
        <Paper p="md" withBorder>
          <Title order={3} mb="md">
            Raw Result Data
          </Title>
          <Code block>{JSON.stringify(result, null, 2)}</Code>
        </Paper>
      </Stack>
    </Container>
  );
}
